import React from 'react';
import PrimaryMailFolderTreeContainer from './PrimaryMailFolderTreeContainer';
import { ArchiveMailFolderTreeContainer, SharedFolderTreeParentContainer } from '../lazyFunctions';
import { isCapabilityEnabled } from 'owa-capabilities';
import { archiveCapability } from 'owa-capabilities-definitions/lib/archiveCapability';
import { sharedCapability } from 'owa-capabilities-definitions/lib/sharedCapability';
import { observer } from 'owa-mobx-react';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { FolderHierarchyConfigurationQueryDocument } from '../graphql/__generated__/FolderHierarchyConfigurationQuery.interface';
import { useManagedQuery } from 'owa-apollo-hooks/lib/useManagedQuery';
import type { MailboxInfo } from 'owa-client-types';
import { GroupsSwitchOrGotoCTA } from 'owa-group-left-nav-mail';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { getDensityMode } from 'owa-fabric-theme';
import { BootState, getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { lazyGetAccountInformation } from 'owa-storage-store';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import { loadMailFolders } from 'owa-mail-folder-store';
import { loadFavorites } from 'owa-favorites';
import { Spinner } from '@fluentui/react-components';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';

import { isAccountExplicitLogon } from 'owa-account-shared-mailbox-utils';

export interface MailFolderTreesParentContainerProps {
    favoritesNodeCount: number;
    accountMailboxInfo: MailboxInfo;
    isExpanded?: boolean;
    shouldHideRootNode?: boolean;
    accountBootState: BootState;
    ellipsesOnHover?: boolean;
    shouldHideShared?: boolean;
}

enum densitySpacing {
    Compact = '12px',
    Full = '12px',
    Simple = '8px',
}

/**
 * Parent container component hosting primary, archive and shared folder tree containers
 */
const defaultExport: React.FunctionComponent<MailFolderTreesParentContainerProps> = observer(
    function MailFolderTreesParentContainer(props: MailFolderTreesParentContainerProps) {
        const {
            isExpanded,
            favoritesNodeCount,
            accountMailboxInfo,
            shouldHideRootNode,
            accountBootState,
            ellipsesOnHover,
            shouldHideShared,
        } = props;
        const globalSettingsAccountMailboxInfo = getGlobalSettingsAccountMailboxInfo();
        const isPrimaryAcount =
            getIndexerValueForMailboxInfo(accountMailboxInfo) ===
            getIndexerValueForMailboxInfo(globalSettingsAccountMailboxInfo);
        const isPreRenderComplete = accountBootState === BootState.StartupComplete;
        const densityMode = useComputedValue(() => {
            return getDensityMode();
        }, []);
        const monarchMultiAccountEnabled = isMonarchMultipleAccountsEnabled();

        const accountPaddingStyle = React.useMemo(
            () =>
                !monarchMultiAccountEnabled
                    ? { paddingBottom: densitySpacing[densityMode] }
                    : undefined,
            [densityMode, monarchMultiAccountEnabled]
        );

        React.useEffect(() => {
            (async () => {
                // For primary accounts, this happens on boot
                // For non-primary accounts we have to load it
                if (!isPrimaryAcount && isPreRenderComplete) {
                    await loadMailFolders(accountMailboxInfo);

                    loadFavorites(accountMailboxInfo);

                    lazyGetAccountInformation.importAndExecute(accountMailboxInfo);
                }
            })();
        }, [accountMailboxInfo, isPreRenderComplete]);

        const { loading, data } = useManagedQuery(FolderHierarchyConfigurationQueryDocument, {
            variables: {
                mailboxInfo: accountMailboxInfo,
            },
        });
        //Special case: display spinner if prerender is not completed
        if (loading || !data || !isPreRenderComplete) {
            return <Spinner size="tiny" />;
        }

        // Primary root node should be shown in case of any one of the Favorites or Groups or Archive mailbox or Shared Folders is shown
        const shouldShowPrimaryRootNode =
            !shouldHideRootNode && getIsPrimaryRootShown(favoritesNodeCount > 0);

        const userConfig = data?.userConfiguration;
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE!
         *	> Optional chain expressions can return undefined by design - using a non-null assertion is unsafe and wrong. */
        const archiveMailboxGuid = userConfig?.SessionSettings?.ArchiveMailboxGuid!;
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE!
         *	> Optional chain expressions can return undefined by design - using a non-null assertion is unsafe and wrong. */
        const organizationDomain = userConfig?.SessionSettings?.OrganizationDomain!;
        const hasArchive = userConfig?.SessionSettings?.HasArchive;
        const showOnlineArchiveEnabled = userConfig?.PolicySettings?.ShowOnlineArchiveEnabled;
        const shouldShowArchiveTree = hasArchive && showOnlineArchiveEnabled;
        const accountDisplayName = userConfig?.SessionSettings?.UserDisplayName;
        const isExplicitLogon = isAccountExplicitLogon(accountMailboxInfo);

        return (
            <div role="group">
                <PrimaryMailFolderTreeContainer
                    showRootNode={shouldShowPrimaryRootNode}
                    isMailRootFolderTreeCollapsed={
                        userConfig?.UserOptions?.IsMailRootFolderTreeCollapsed
                    }
                    isExplicitLogon={isExplicitLogon}
                    userEmailAddress={userConfig?.SessionSettings?.UserEmailAddress}
                    mailboxInfo={accountMailboxInfo}
                    isExpanded={isExpanded}
                    style={accountPaddingStyle}
                    ellipsesOnHover={ellipsesOnHover}
                />
                {isCapabilityEnabled(archiveCapability, accountMailboxInfo) &&
                    shouldShowArchiveTree && (
                        <ArchiveMailFolderTreeContainer
                            archiveMailboxGuid={archiveMailboxGuid}
                            organizationDomain={organizationDomain}
                            primaryMailboxInfo={accountMailboxInfo}
                            userEmailAddress={accountMailboxInfo.mailboxSmtpAddress}
                            style={accountPaddingStyle}
                            ellipsesOnHover={ellipsesOnHover}
                            userDisplayName={accountDisplayName}
                            archiveServiceDisplayName={
                                userConfig?.SessionSettings?.ArchiveDisplayName
                            }
                            explicitLogonMailboxSmtpAddress={
                                isExplicitLogon ? accountMailboxInfo.mailboxSmtpAddress : undefined
                            }
                        />
                    )}

                {isCapabilityEnabled(sharedCapability, accountMailboxInfo) && (
                    <>
                        {!shouldHideShared && (
                            <SharedFolderTreeParentContainer
                                shouldRenderRootNode={monarchMultiAccountEnabled}
                                accountMailboxInfo={accountMailboxInfo}
                                primaryMailboxInfo={accountMailboxInfo}
                                style={accountPaddingStyle}
                                ellipsesOnHover={ellipsesOnHover}
                            />
                        )}
                    </>
                )}
                {isGroupsEnabled(accountMailboxInfo) && (
                    <GroupsSwitchOrGotoCTA mailboxInfo={accountMailboxInfo} />
                )}
            </div>
        );
    },
    'MailFolderTreesParentContainer'
);
export default defaultExport;

function getIsPrimaryRootShown(isFavoriteShown: boolean): boolean {
    return isFavoriteShown || isGroupsEnabled() || !isConsumer();
}
